'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Tooltip, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../../contexts/translation.context';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { RevshareOffer } from '../types';
import {
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
  GlobalIc,
} from '../../../../components/icons';

interface StatisticsCellProps {
  offer: RevshareOffer;
}

// Get source icon
const getSourceIcon = (source: string, size: number = 16) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    onlyfans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    facebook: <Icon icon="ic:baseline-facebook" width={size} className="text-neutral-8" />,
    tinder: <Icon icon="simple-icons:tinder" width={size} className="text-neutral-8" />,
    another: <IcAnotherSoc size={size} />,
  };

  return iconMap[source?.toLowerCase()] || <GlobalIc size={size} />;
};

// Get source label
const getSourceLabel = (source: string) => {
  const labelMap: Record<string, string> = {
    only_fans: 'OnlyFans',
    onlyfans: 'OnlyFans',
    reddit: 'Reddit',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    telegram: 'Telegram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    facebook: 'Facebook',
    tinder: 'Tinder',
    another: 'Другое',
  };

  return labelMap[source?.toLowerCase()] || source;
};

const StatisticsCell: React.FC<StatisticsCellProps> = ({ offer }) => {
  const { t } = useTranslations();
  const [isActiveCampaignsOpen, setIsActiveCampaignsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Popover hover handlers
  const openActiveCampaigns = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsActiveCampaignsOpen(true);
  };

  const scheduleActiveCampaignsClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsActiveCampaignsOpen(false);
    }, 150);
  };

  // Get statistics from of_data or offer
  const arpu = offer.of_data?.arpu;
  const conversion = offer.of_data?.conversion;
  const activeCampaigns = offer.ref_share_active_total ?? offer.revshare_campaigns_count ?? 0;
  const activeCampaignsBySource = offer.ref_share_active_by_source || {};
  const sfsLevel = offer.sfs_posts_level || 'None';

  // Check if there are active campaigns by source
  const hasActiveCampaignsBySource = Object.keys(activeCampaignsBySource).length > 0;

  // Format ARPU
  const formatArpu = (value?: number) => {
    if (value === undefined || value === null) {
      return t('partner.revshare.offers.statistics.noData') || 'Данных нет';
    }
    return `$${value.toFixed(2)}`;
  };

  // Format conversion
  const formatConversion = (value?: number) => {
    if (value === undefined || value === null) {
      return t('partner.revshare.offers.statistics.noData') || 'Данных нет';
    }
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="flex flex-col gap-1">
      {/* ARPU */}
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-auth-muted text-xs">
          {t('partner.revshare.offers.statistics.arpu') || 'ARPU'}:
        </Typography>
        <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
          {formatArpu(arpu)}
        </Typography>
      </div>

      {/* Conversion */}
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-auth-muted text-xs">
          {t('partner.revshare.offers.statistics.conversion') || 'Конверсия'}:
        </Typography>
        <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
          {formatConversion(conversion)}
        </Typography>
        <ResponsiveTooltip
          content={
            t('partner.revshare.offers.statistics.conversionInfo') ||
            'Клик по ссылке/подписчики за последние 7 дней'
          }
        >
          <Icon
            icon="solar:info-circle-bold"
            width={14}
            className="text-neutral-500 cursor-pointer"
          />
        </ResponsiveTooltip>
      </div>

      {/* Active campaigns */}
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-auth-muted text-xs">
          {t('partner.revshare.offers.statistics.activeCampaigns') || 'Активные кампании'}:
        </Typography>
        <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
          {activeCampaigns}
        </Typography>
        {hasActiveCampaignsBySource ? (
          <Popover
            placement="bottom-start"
            offset={8}
            showArrow={false}
            isOpen={isActiveCampaignsOpen}
            onOpenChange={setIsActiveCampaignsOpen}
            shouldCloseOnBlur
            isDismissable
          >
            <PopoverTrigger>
              <div
                className="cursor-pointer"
                onMouseEnter={openActiveCampaigns}
                onMouseLeave={scheduleActiveCampaignsClose}
              >
                <Icon icon="solar:info-circle-bold" width={14} className="text-neutral-500" />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 bg-white border border-light rounded-3 shadow-[0px_0px_11px_rgba(0,0,0,0.1)]"
              onMouseEnter={openActiveCampaigns}
              onMouseLeave={scheduleActiveCampaignsClose}
            >
              <div className="flex flex-col pb-4">
                <div className="px-4 pt-4">
                  <Typography variant="body2" className="text-auth-ink text-sm font-normal">
                    {t('partner.revshare.offers.statistics.activeCampaignsTitle') ||
                      'Активные кампании:'}
                  </Typography>
                </div>
                <div className="flex flex-col px-4">
                  {Object.entries(activeCampaignsBySource).map(([source, count]) => (
                    <div key={source} className="flex items-center gap-2 h-8">
                      {getSourceIcon(source)}
                      <Typography variant="body2" className="text-auth-ink text-sm">
                        {getSourceLabel(source)}({count})
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <ResponsiveTooltip
            content={
              t('partner.revshare.offers.statistics.activeCampaignsInfo') ||
              'Количество активных кампаний'
            }
          >
            <Icon
              icon="solar:info-circle-bold"
              width={14}
              className="text-neutral-500 cursor-pointer"
            />
          </ResponsiveTooltip>
        )}
      </div>

      {/* SFS Posts */}
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-auth-muted text-xs">
          {t('partner.revshare.offers.statistics.postsSFS') || 'Посты SFS'}:
        </Typography>
        <ResponsiveTooltip
          content={
            t('partner.revshare.offers.statistics.postsSFSTooltip') ||
            'Нет сообщений в SFS или сообщений на 1 аккаунте за последние 7 дней'
          }
        >
          <Typography
            variant="body3"
            className="text-auth-ink text-sm font-semibold cursor-pointer"
          >
            {sfsLevel}
          </Typography>
        </ResponsiveTooltip>
      </div>
    </div>
  );
};

export default StatisticsCell;
